import { QueryClient, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { subscribersEndpoints as endpoints, broadcastsEndpoints } from '@/api/endpoints';
import { callGet, callPut } from '@/api/fetcher';
import { getNextCursor } from '@/hooks/queryCache/nextCursor';
import { getPermissions } from '@/redux/permissions/selectors';
import { Subscriber, Subscribers } from '@/shared/types/broadcast';
import { IntegrationType } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import { useBroadcasts } from './useBroadcasts';
import { selectIsDraft, selectValidSubscribers } from '../redux/selectors';

interface UpdateProps {
  subscribers: Partial<Subscriber>[];
  channel: IntegrationType;
  broadcast_id: string;
}

interface ErrorSubscriber {
  field: string;
  message: string;
  index: number;
  value: string;
}

interface VerifyProps {
  channel: IntegrationType;
  subscribers: Partial<Subscriber>[];
}
export const API = Object.freeze({
  listSubscribers: async (
    broadcastId: string,
    cursor?: { cursor: string },
    filter?: string,
    filterStatus?: string
  ): Promise<Subscribers> =>
    callGet(endpoints.subscribers(broadcastId, cursor.cursor, filter, filterStatus)),

  updateSubscribers: async ({ broadcast_id, ...data }: UpdateProps): Promise<Subscribers> =>
    callPut(endpoints.subscribersUpsert(broadcast_id), data),
  verifySubscribers: async ({ ...data }: UpdateProps): Promise<ErrorSubscriber[]> =>
    callPut(endpoints.subscribersVerify, data),
});

export const onSubscribersUpdated = (
  queryClient: QueryClient,
  broadcast_id: string,
  subscribers: Subscribers
) => {
  queryClient.setQueryData<Subscribers>([endpoints.subscribers(broadcast_id)], () => subscribers);
};

export const useSubscribers = (broadcastId?: string, filter = '', filterStatus = null) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const canRead = useSelector((state: RootState) =>
    getPermissions(state, 'broadcasts', actions.READ)
  );

  const isDraft = useSelector(selectIsDraft);
  const validSubscribers = useSelector(selectValidSubscribers);
  const { broadcast: APIbroadcast } = useBroadcasts({ broadcastId });

  const {
    data: subscribers,
    status: listStatus,
    isLoading,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: [endpoints.subscribers(broadcastId), filter, filterStatus],
    queryFn: async ({ pageParam }) => {
      return API.listSubscribers(broadcastId, pageParam, filter, filterStatus);
    },
    getNextPageParam: getNextCursor,
    initialPageParam: {} as { cursor: string | undefined },
    enabled: !!broadcastId && canRead && broadcastId !== 'draft',
    // Smart conditional polling: only poll when broadcast is actively processing
    refetchInterval: () => {
      const broadcastStatus = APIbroadcast?.status;
      // Only refetch if broadcast is actively being processed
      if (broadcastStatus === 'in_progress' || broadcastStatus === 'ready') {
        return 7 * 1000; // Keep original 7 seconds for active broadcasts
      }
      // No automatic refetching for completed, sent, or draft broadcasts
      return false;
    },
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
  });

  const { mutate: verifySubscribers, status: verifyStatus } = useMutation<
    ErrorSubscriber[],
    Error,
    VerifyProps
  >({ mutationFn: API.verifySubscribers, meta: { errorToast: true } });

  const { mutateAsync: updateSubscribersAsync, status: updateStatus } = useMutation<
    Subscribers,
    Error,
    UpdateProps
  >({
    mutationFn: API.updateSubscribers,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [endpoints.subscribers(broadcastId), broadcastsEndpoints.broadcast(broadcastId)],
      });

      toast.success(t('broadcasts.recipients_updated'));
    },
    meta: { errorToast: true },
  });

  const BATCH_SIZE = 5000;

  const updateBroadcastSubscribersAsync = async ({ broadcast_id, subscribers, channel }) => {
    if (subscribers.length === 0) {
      await updateSubscribersAsync({ broadcast_id, subscribers: [], channel });
    } else {
      for (let i = 0; i < subscribers.length; i += BATCH_SIZE) {
        const batch = subscribers.slice(i, i + BATCH_SIZE);
        try {
          await updateSubscribersAsync({ broadcast_id, subscribers: batch, channel });
        } catch (error) {
          console.error('Failed to update batch', error);
          toast.error(error.message);
        }
      }
    }
  };
  const validSubscribersCount = isDraft
    ? validSubscribers.length
    : APIbroadcast?.subscribers_count + validSubscribers.length;

  return {
    verifySubscribers,
    verifyStatus,
    subscribers,
    fetchNextPage,
    hasNextPage,
    listStatus,
    isLoading,
    updateStatus,
    updateSubscribersAsync: updateBroadcastSubscribersAsync,
    validSubscribersCount,
  };
};
